import { useEffect, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { updateDateRange } from "../../shared/filter/filterSlice"
import styles from "./DateStrip.module.css"

const DAY_NAMES = ["ВС", "ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ"]
const MONTH_NAMES = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"]
const WEEKENDS = [0, 6]

function buildDays(count = 30) {
  const days = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  for (let i = 0; i < count; i++) {
    const d = new Date(today)
    d.setDate(today.getDate() + i)
    days.push(d)
  }
  return days
}

function toDateOnly(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`
}
function toDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}
function startOfDay(d: Date) { const r = new Date(d); r.setHours(0,0,0,0); return r }
function endOfDay(d: Date) { const r = new Date(d); r.setHours(23,59,59,0); return r }

type SpecialId = "today" | "tomorrow" | null

export function DateStrip() {
  const dispatch = useAppDispatch()
  const scrollRef = useRef<HTMLDivElement>(null)
  // По умолчанию ничего не выбрано — показываем все события
  const [activeSpecial, setActiveSpecial] = useState<SpecialId>(null)
  const [activeDate, setActiveDate] = useState<Date | null>(null)
  // Синхронизация: если даты сброшены снаружи (кнопка «Все») — сбрасываем и локальный стейт
  const startDate = useAppSelector(state => state.filter.startDate)
  useEffect(() => {
    if (!startDate) {
      setActiveSpecial(null)
      setActiveDate(null)
    }
  }, [startDate])

  const days = buildDays(30)

  // Свайп
  const touchStartX = useRef(0)
  const touchStartScroll = useRef(0)

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartScroll.current = scrollRef.current?.scrollLeft || 0
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!scrollRef.current) return
    const dx = touchStartX.current - e.touches[0].clientX
    scrollRef.current.scrollLeft = touchStartScroll.current + dx
  }

  const selectSpecial = (id: SpecialId) => {
    // Повторный клик — снимаем выбор
    if (activeSpecial === id) {
      setActiveSpecial(null)
      setActiveDate(null)
      dispatch(updateDateRange([undefined, undefined]))
      return
    }
    setActiveSpecial(id)
    setActiveDate(null)
    const today = new Date()
    if (id === "today") {
      dispatch(updateDateRange([toDateOnly(today), toDateOnly(today)]))
    } else if (id === "tomorrow") {
      const tomorrow = new Date(today)
      tomorrow.setDate(today.getDate() + 1)
      dispatch(updateDateRange([toDateOnly(tomorrow), toDateOnly(tomorrow)]))
    }
  }

  const selectDay = (day: Date) => {
    // Повторный клик — снимаем выбор
    if (activeDate?.toDateString() === day.toDateString()) {
      setActiveDate(null)
      setActiveSpecial(null)
      dispatch(updateDateRange([undefined, undefined]))
      return
    }
    setActiveSpecial(null)
    setActiveDate(day)
    dispatch(updateDateRange([toDateOnly(day), toDateOnly(day)]))
  }

  const isWeekend = (d: Date) => WEEKENDS.includes(d.getDay())

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return
    scrollRef.current.scrollLeft += dir === "left" ? -200 : 200
  }

  return (
    <div className={styles.wrapper}>
      <button
        className={`${styles.special} ${activeSpecial === "today" ? styles.specialActive : styles.specialOutline}`}
        onClick={() => selectSpecial("today")}
      >
        Сегодня
      </button>
      <button
        className={`${styles.special} ${styles.specialOutline} ${activeSpecial === "tomorrow" ? styles.specialOutlineActive : ""}`}
        onClick={() => selectSpecial("tomorrow")}
      >
        Завтра
      </button>

      <div className={styles.divider} />

      <button className={styles.arrow} onClick={() => scroll("left")}>‹</button>

      <div
        className={styles.scroll}
        ref={scrollRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        {days.map((day, i) => {
          const isActive = activeDate?.toDateString() === day.toDateString()
          const weekend = isWeekend(day)
          return (
            <button
              key={i}
              className={`${styles.day} ${isActive ? styles.dayActive : ""} ${weekend && !isActive ? styles.dayWeekend : ""}`}
              onClick={() => selectDay(day)}
            >
              <span className={styles.dayNum}>{day.getDate()} {MONTH_NAMES[day.getMonth()]}</span>
              <span className={styles.dayName}>{DAY_NAMES[day.getDay()]}</span>
            </button>
          )
        })}
      </div>

      <button className={styles.arrow} onClick={() => scroll("right")}>›</button>
    </div>
  )
}
